import React from 'react';
import { Modal, Button, Typography } from 'antd';
import { PrinterOutlined, EyeOutlined, CheckSquareOutlined, MinusSquareOutlined } from '@ant-design/icons';
import { KittingTagsData } from '../../../../redux/api/order-line-item';
import KittingTagPicker from '../KittingTagPicker';
import { useKittingTagSelection } from '../KittingTagPicker/useKittingTagSelection';

const { Text } = Typography;

interface IProps {
	open: boolean;
	loading: boolean;
	data: KittingTagsData | null;
	onCancel: () => void;
	onPrint: (
		toolIndices: number[],
		jawIndices: number[],
		otherTagKeys: string[]
	) => void;
	onPreview: (
		toolIndices: number[],
		jawIndices: number[],
		otherTagKeys: string[]
	) => void;
	previewLoading?: boolean;
}

const IndividualTagsModal: React.FC<IProps> = ({
	open,
	loading,
	data,
	onCancel,
	onPrint,
	onPreview,
	previewLoading = false,
}) => {
	const selection = useKittingTagSelection(data);
	const { totalSelected, allSelected } = selection;
	const hasSelection = totalSelected > 0;

	const handlePrint = () => {
		const { toolIndices, jawIndices, otherTagKeys } = selection.selectedTags();
		onPrint(toolIndices, jawIndices, otherTagKeys);
		selection.clear();
	};

	const handlePreview = () => {
		const { toolIndices, jawIndices, otherTagKeys } = selection.selectedTags();
		onPreview(toolIndices, jawIndices, otherTagKeys);
	};

	const handleCancel = () => {
		selection.clear();
		onCancel();
	};

	return (
		<Modal
			open={open}
			onCancel={handleCancel}
			width={900}
			title={
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						paddingRight: 24,
					}}
				>
					<div>
						<Text strong style={{ fontSize: 18 }}>
							{data?.order_name} — {data?.line_item_name}
						</Text>
						<br />
						<Text type="secondary" style={{ fontSize: 14 }}>
							Select the tags you want to print
						</Text>
					</div>
					<Button
						icon={allSelected ? <MinusSquareOutlined /> : <CheckSquareOutlined />}
						onClick={selection.toggleEverything}
						style={{ minHeight: 44, minWidth: 44, fontSize: 14 }}
					>
						{allSelected ? 'Deselect All' : 'Select All'}
					</Button>
				</div>
			}
			footer={
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 10,
						padding: '4px 0',
					}}
				>
					<Button
						size="large"
						onClick={handleCancel}
						style={{ minHeight: 48, fontSize: 15 }}
					>
						Cancel
					</Button>
					{import.meta.env.DEV && (
						<Button
							size="large"
							icon={<EyeOutlined />}
							disabled={!hasSelection}
							loading={previewLoading}
							onClick={handlePreview}
							style={{ minHeight: 48, fontSize: 15 }}
						>
							Preview
						</Button>
					)}
					<Button
						data-testid="fastlane-kit-print-selected"
						size="large"
						type="primary"
						icon={<PrinterOutlined />}
						disabled={!hasSelection}
						loading={loading}
						onClick={handlePrint}
						style={{ minHeight: 48, minWidth: 120, fontSize: 15 }}
					>
						Print ({totalSelected})
					</Button>
				</div>
			}
		>
			<div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', paddingRight: 4 }}>
				<KittingTagPicker data={data} selection={selection} />
			</div>
		</Modal>
	);
};

export default IndividualTagsModal;
